// Registre des immobilisations : valeur d'acquisition, dotation de l'exercice, cumul des amortissements, valeur nette comptable.
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, FormControlLabel, Grid, InputAdornment, LinearProgress, MenuItem, Switch, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import {
  AccountBalanceOutlined, AddOutlined, DomainOutlined, PrintOutlined, SearchOutlined, TrendingDownOutlined, WarningAmberOutlined,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Kpi, Panel } from '../../components/ui';
import { amortAPI } from '../../services/api';
import { apiError, dateFr, fcfa } from '../../utils/format';
import { tokens } from '../../theme';
import ImmoDialog from './ImmoDialog';

const M = ({ v, bold, color }) => <Box component="span" sx={{ fontFamily: tokens.mono, whiteSpace: 'nowrap', fontWeight: bold ? 700 : undefined, color }}>{v}</Box>;

const Amortissements = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [exercice, setExercice] = useState(new Date().getFullYear());
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [info, setInfo] = useState(location.state?.info || '');
  const [cat, setCat] = useState('');
  const [q, setQ] = useState('');
  const [tous, setTous] = useState(false);
  const [dlg, setDlg] = useState(false);

  const load = () => amortAPI.list({ exercice, tous: tous ? 1 : undefined }).then((r) => setD(r.data)).catch((e) => setError(apiError(e)));
  useEffect(() => { load(); }, [exercice, tous]); // eslint-disable-line

  const liste = useMemo(() => (d?.immobilisations || []).filter((i) => (!cat || i.categorie === cat)
    && (!q || `${i.designation} ${i.reference || ''} ${i.fournisseur || ''} ${i.localisation || ''}`.toLowerCase().includes(q.toLowerCase()))), [d, cat, q]);
  const t = d?.totaux;
  const s = (k) => liste.reduce((x, i) => x + (Number(i[k]) || 0), 0);

  return (
    <Layout title="Immobilisations & amortissements" plain
      actions={(
        <>
          <TextField select size="small" label="Exercice" value={exercice} onChange={(e) => setExercice(Number(e.target.value))} sx={{ minWidth: 110 }}>
            {(d?.exercices || [exercice]).map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}
          </TextField>
          <Button startIcon={<PrintOutlined />} onClick={() => window.print()}>Imprimer</Button>
          <Button variant="contained" startIcon={<AddOutlined />} disabled={!d} onClick={() => setDlg(true)}>Ajouter un équipement</Button>
        </>
      )}>
      {info && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo('')}>{info}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {t && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6} md={3}><Kpi compact icon={DomainOutlined} tint="navy" label="Valeur d'acquisition" value={fcfa(t.valeur)} valueSize={21} sub={`F · ${t.nombre} équipement(s) en service`} /></Grid>
          <Grid item xs={6} md={3}><Kpi compact icon={TrendingDownOutlined} tint="amber" label={`Dotation ${exercice}`} value={fcfa(t.dotation_exercice)} valueSize={21} sub="F — charge de l'exercice" /></Grid>
          <Grid item xs={6} md={3}><Kpi compact icon={TrendingDownOutlined} tint="gray" label="Amortissements cumulés" value={fcfa(t.cumul)} valueSize={21} sub="F à ce jour" /></Grid>
          <Grid item xs={6} md={3}><Kpi compact icon={AccountBalanceOutlined} tint="green" label="Valeur nette comptable" value={fcfa(t.vnc)} valueSize={21} sub="F à ce jour" /></Grid>
        </Grid>
      )}
      {t && (t.fin_proche > 0 || t.totalement_amortis > 0) && (
        <Alert severity="info" icon={<WarningAmberOutlined />} sx={{ mb: 2 }}>
          {t.totalement_amortis > 0 && `${t.totalement_amortis} équipement(s) totalement amorti(s) mais toujours en service. `}
          {t.fin_proche > 0 && `${t.fin_proche} équipement(s) finissent leur amortissement dans moins de 12 mois : pensez au renouvellement.`}
        </Alert>
      )}
      {d?.par_categorie.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }} className="no-print">
          <Chip label="Toutes catégories" color={!cat ? 'primary' : 'default'} variant={!cat ? 'filled' : 'outlined'} onClick={() => setCat('')} />
          {d.par_categorie.map((c) => (
            <Chip key={c.categorie} label={`${c.label} · ${c.nombre}`} color={cat === c.categorie ? 'primary' : 'default'} variant={cat === c.categorie ? 'filled' : 'outlined'}
              onClick={() => setCat(cat === c.categorie ? '' : c.categorie)} />
          ))}
        </Box>
      )}
      <Panel flush>
        <Box className="no-print" sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <TextField size="small" placeholder="Désignation, référence, fournisseur…" value={q} onChange={(e) => setQ(e.target.value)} sx={{ minWidth: 260 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchOutlined fontSize="small" /></InputAdornment> }} />
          <FormControlLabel control={<Switch size="small" checked={tous} onChange={(e) => setTous(e.target.checked)} />} label="Inclure les équipements sortis" />
        </Box>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Équipement</TableCell><TableCell>Mise en service</TableCell><TableCell align="right">Prix (F)</TableCell>
                <TableCell align="right">Durée</TableCell><TableCell align="right">Dotation {exercice}</TableCell><TableCell align="right">Cumul</TableCell>
                <TableCell align="right">VNC</TableCell><TableCell sx={{ minWidth: 150 }}>Amorti</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {liste.map((i) => (
                <TableRow key={i.id} hover sx={{ cursor: 'pointer', opacity: i.statut === 'EN_SERVICE' ? 1 : 0.6 }} onClick={() => navigate(`/amortissements/${i.id}`)}>
                  <TableCell>
                    <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{i.designation}{i.quantite > 1 ? ` × ${i.quantite}` : ''}</Typography>
                    <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{i.categorie_label}{i.reference ? ` · ${i.reference}` : ''}{i.statut !== 'EN_SERVICE' ? ` · ${i.statut_label} le ${dateFr(i.date_sortie)}` : ''}</Typography>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{dateFr(i.date_mise_en_service)}</TableCell>
                  <TableCell align="right"><M v={fcfa(i.valeur_acquisition)} /></TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{i.amortissable ? `${i.duree_annees} ans` : <Typography component="span" sx={{ fontSize: 12.5, color: 'text.secondary' }}>non amort.</Typography>}</TableCell>
                  <TableCell align="right"><M v={i.dotation_exercice ? fcfa(i.dotation_exercice) : '—'} /></TableCell>
                  <TableCell align="right"><M v={fcfa(i.cumul)} /></TableCell>
                  <TableCell align="right"><M v={fcfa(i.vnc)} bold /></TableCell>
                  <TableCell>
                    {i.amortissable ? (
                      <Box>
                        <LinearProgress variant="determinate" value={Math.min(100, i.pct_amorti)} color={i.totalement_amorti ? 'success' : i.reste_mois <= 12 ? 'warning' : 'primary'} sx={{ height: 7, borderRadius: 4 }} />
                        <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.25 }}>
                          {String(i.pct_amorti).replace('.', ',')} % · {i.totalement_amorti ? 'terminé' : i.statut !== 'EN_SERVICE' ? 'arrêté' : `fin ${dateFr(i.fin_amortissement)}`}
                        </Typography>
                      </Box>
                    ) : '—'}
                  </TableCell>
                </TableRow>
              ))}
              {d && !liste.length && (
                <TableRow><TableCell colSpan={8} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                  {d.immobilisations.length ? 'Aucun équipement ne correspond.' : "Registre vide. Ajoutez vos équipements : centrale solaire, forage, terrain, monorail, moules, véhicules, camions, bâtiments, engins…"}
                </TableCell></TableRow>
              )}
              {liste.length > 1 && (
                <TableRow sx={{ '& td': { fontWeight: 700, borderTop: `2px solid ${tokens.line}` } }}>
                  <TableCell colSpan={2}>Total ({liste.length})</TableCell><TableCell align="right"><M v={fcfa(s('valeur_acquisition'))} /></TableCell><TableCell />
                  <TableCell align="right"><M v={fcfa(s('dotation_exercice'))} /></TableCell><TableCell align="right"><M v={fcfa(s('cumul'))} /></TableCell>
                  <TableCell align="right"><M v={fcfa(s('vnc'))} /></TableCell><TableCell />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Panel>
      <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 1.5 }}>
        Amortissement linéaire au prorata temporis (mois de 30 jours) à partir de la date de mise en service. Cumul et VNC à ce jour ; dotation sur l'exercice {exercice}.
      </Typography>
      <ImmoDialog open={dlg} categories={d?.categories} statuts={d?.statuts} onClose={() => setDlg(false)}
        onSaved={(r, id) => { setDlg(false); navigate(`/amortissements/${id}`, { state: { info: r.message } }); }} />
    </Layout>
  );
};

export default Amortissements;
